import React, { useState, useEffect } from 'react';
import { BarChart, PieChart, RefreshCw, Download, Calendar, ArrowUpDown } from 'lucide-react';

interface ChatbotAnalytics {
  totalMessages: number;
  userMessages: number;
  botMessages: number;
  positiveRatings: number;
  negativeRatings: number;
  commonQueries: Record<string, number>;
}

interface ApiStats {
  queries: Record<string, number>;
  feedback: {
    positive: number;
    negative: number;
    total: number;
  };
}

const ANALYTICS_KEY = 'advisorgrow_chat_analytics';

const ChatbotAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<ChatbotAnalytics>({
    totalMessages: 0,
    userMessages: 0,
    botMessages: 0,
    positiveRatings: 0,
    negativeRatings: 0,
    commonQueries: {}
  });
  const [apiStats, setApiStats] = useState<ApiStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState<'all' | 'week' | 'month'>('all');
  const [appointmentStats, setAppointmentStats] = useState({
    total: 0,
    completed: 0,
    upcoming: 0,
    cancelled: 0
  });

  useEffect(() => {
    loadAnalytics();
    fetchApiStats();
    fetchAppointmentStats();
  }, []);

  const loadAnalytics = () => {
    setLoading(true);
    const savedAnalytics = localStorage.getItem(ANALYTICS_KEY);
    if (savedAnalytics) {
      try {
        setAnalytics(JSON.parse(savedAnalytics));
      } catch (error) {
        console.error('Error parsing saved analytics:', error);
      }
    }
    setLoading(false);
  };

  const fetchApiStats = async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chatbot`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({ message: 'stats' })
        }
      );

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`);
      }

      const data = await response.json();
      if (data.stats) {
        setApiStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching API stats:', error);
    }
  };

  const fetchAppointmentStats = async () => {
    // In a real implementation, this would fetch appointment stats from the database
    // For now, we'll use mock data
    setAppointmentStats({
      total: 24,
      completed: 18,
      upcoming: 5,
      cancelled: 1
    });
  };

  const downloadAnalytics = () => {
    const combinedData = {
      clientAnalytics: analytics,
      serverAnalytics: apiStats,
      appointmentStats: appointmentStats
    };
    
    const dataStr = JSON.stringify(combinedData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `chatbot_analytics_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const resetAnalytics = () => {
    if (confirm('Are you sure you want to reset all analytics data? This cannot be undone.')) {
      const emptyAnalytics = {
        totalMessages: 0,
        userMessages: 0,
        botMessages: 0,
        positiveRatings: 0,
        negativeRatings: 0,
        commonQueries: {}
      };
      setAnalytics(emptyAnalytics);
      localStorage.setItem(ANALYTICS_KEY, JSON.stringify(emptyAnalytics));
    }
  };

  // Calculate satisfaction rate
  const totalRatings = analytics.positiveRatings + analytics.negativeRatings;
  const satisfactionRate = totalRatings > 0 
    ? Math.round((analytics.positiveRatings / totalRatings) * 100) 
    : 0;

  // Sort common queries by frequency
  const sortedQueries = Object.entries(analytics.commonQueries)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  // Combine client and server stats for a more complete picture
  const combinedQueries: Record<string, number> = { ...analytics.commonQueries };
  if (apiStats?.queries) {
    Object.entries(apiStats.queries).forEach(([key, value]) => {
      combinedQueries[key] = (combinedQueries[key] || 0) + value;
    });
  }

  const sortedCombinedQueries = Object.entries(combinedQueries)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Chatbot Analytics</h2>
        <div className="flex space-x-2">
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value as any)}
            className="border border-gray-300 rounded-md text-sm px-3 py-2 mr-2"
          >
            <option value="all">All Time</option>
            <option value="month">Last 30 Days</option>
            <option value="week">Last 7 Days</option>
          </select>
          <button 
            onClick={() => {
              loadAnalytics();
              fetchApiStats();
              fetchAppointmentStats();
            }}
            className="p-2 text-gray-500 hover:text-primary rounded-full"
            title="Refresh analytics"
          >
            <RefreshCw className="h-5 w-5" />
          </button>
          <button 
            onClick={downloadAnalytics}
            className="p-2 text-gray-500 hover:text-primary rounded-full"
            title="Download analytics"
          >
            <Download className="h-5 w-5" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <RefreshCw className="h-8 w-8 text-primary animate-spin" />
        </div>
      ) : (
        <div className="space-y-8">
          {/* Overview Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-3xl font-bold text-primary">{analytics.totalMessages}</div>
              <div className="text-gray-600">Total Messages</div>
              <div className="text-xs text-gray-500 mt-1">Client-side tracking</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-3xl font-bold text-primary">{satisfactionRate}%</div>
              <div className="text-gray-600">Satisfaction Rate</div>
              <div className="text-xs text-gray-500 mt-1">Based on {totalRatings} ratings</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-3xl font-bold text-primary">{analytics.userMessages}</div>
              <div className="text-gray-600">User Queries</div>
              <div className="text-xs text-gray-500 mt-1">From this browser</div>
            </div>
          </div>

          {/* Appointment Stats */}
          <div>
            <h3 className="text-lg font-medium mb-4">Appointment Analytics</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-primary">{appointmentStats.total}</div>
                <div className="text-gray-600">Total Appointments</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-green-500">{appointmentStats.completed}</div>
                <div className="text-gray-600">Completed</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-blue-500">{appointmentStats.upcoming}</div>
                <div className="text-gray-600">Upcoming</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-red-500">{appointmentStats.cancelled}</div>
                <div className="text-gray-600">Cancelled</div>
              </div>
            </div>
          </div>

          {/* Server Stats */}
          {apiStats && (
            <div>
              <h3 className="text-lg font-medium mb-4">Server-side Analytics</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-primary">{apiStats.feedback.total}</div>
                  <div className="text-gray-600">Total Feedback</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-primary">{apiStats.feedback.positive}</div>
                  <div className="text-gray-600">Positive Ratings</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-3xl font-bold text-primary">{apiStats.feedback.negative}</div>
                  <div className="text-gray-600">Negative Ratings</div>
                </div>
              </div>
            </div>
          )}

          {/* Feedback Stats */}
          <div>
            <h3 className="text-lg font-medium mb-4">Feedback</h3>
            <div className="flex items-center space-x-4">
              <div className="flex-1 bg-gray-200 rounded-full h-4">
                <div 
                  className="bg-green-500 h-4 rounded-full" 
                  style={{ width: `${satisfactionRate}%` }}
                ></div>
              </div>
              <div className="text-sm">
                <span className="text-green-600 font-medium">{analytics.positiveRatings}</span>
                <span className="text-gray-400 mx-1">/</span>
                <span className="text-red-600 font-medium">{analytics.negativeRatings}</span>
              </div>
            </div>
          </div>

          {/* Common Queries - Combined View */}
          <div>
            <h3 className="text-lg font-medium mb-4">Common Topics (Combined Data)</h3>
            {sortedCombinedQueries.length > 0 ? (
              <div className="space-y-2">
                {sortedCombinedQueries.map(([query, count]) => (
                  <div key={query} className="flex items-center">
                    <div className="w-32 font-medium capitalize">{query}</div>
                    <div className="flex-1 bg-gray-200 rounded-full h-4 mx-2">
                      <div 
                        className="bg-primary h-4 rounded-full" 
                        style={{ 
                          width: `${Math.min(100, (count / sortedCombinedQueries[0][1]) * 100)}%` 
                        }}
                      ></div>
                    </div>
                    <div className="w-8 text-right text-sm">{count}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-center py-4">
                No query data available yet
              </div>
            )}
          </div>

          {/* Usage Over Time (Placeholder) */}
          <div>
            <h3 className="text-lg font-medium mb-4">Usage Over Time</h3>
            <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center h-64">
              <div className="text-center text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Time-series data will be available in a future update</p>
              </div>
            </div>
          </div>

          {/* Reset Button */}
          <div className="pt-4 border-t">
            <button
              onClick={resetAnalytics}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Reset Analytics Data
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotAnalytics;